import axios from 'axios'
import axiosInterceptor from 'services/axiosInterceptor'
import { API_BASE_URL } from 'services/constants'

import type {
	AssetOption,
	DeckFromTemplate,
	DeckSharing,
	ElementDescriptor,
	EntityRef,
	InstantiatedStories,
	PortfolioDeckRequest,
	PortfolioLocations,
	Presentation,
	PresentationScope,
	PresentationSummary,
	PresentationTemplate,
	PresentationVisibility,
	PreviewBlockInput,
	ResolveResponse,
	SavedDeckTemplate,
	SavePresentationInput,
	ShareLink,
	SharedPresentation,
	SharedPresentationError,
	Snapshot,
	SnapshotPreflight,
	SnapshotSummary,
	SourcePhoto,
	Story,
} from '../types/presentation.types'

/**
 * HTTP layer for `/api/presentations` (MYD-493).
 *
 * Pure axios, no Redux. `slices/presentationSlice.ts` wraps the gallery calls
 * in thunks; the editor calls the rest directly.
 *
 * Imports `services/axiosInterceptor` directly rather than through the
 * `services` barrel, for the reason documented in explorerService.ts: the
 * barrel re-creates the DT-029 import cycle.
 */
const api = () => axiosInterceptor(API_BASE_URL)

/** The caller's decks plus partners' shared ones, as summaries (no definitions). */
/**
 * With `anchor`, only decks with a story about that asset (MYD-497), for the
 * "Presentations" menu on an asset's page.
 */
export async function listPresentations(anchor?: EntityRef): Promise<PresentationSummary[]> {
	const { data } = await api().get<PresentationSummary[]>('/presentations', {
		params: anchor ? { anchor: `${anchor.scope}:${anchor.entityId}` } : undefined,
	})
	return data
}

export async function getPresentation(id: string): Promise<Presentation> {
	const { data } = await api().get<Presentation>(`/presentations/${id}`)
	return data
}

export async function createPresentation(input: SavePresentationInput): Promise<Presentation> {
	const { data } = await api().post<Presentation>('/presentations', input)
	return data
}

export async function updatePresentation(id: string, input: SavePresentationInput): Promise<Presentation> {
	const { data } = await api().put<Presentation>(`/presentations/${id}`, input)
	return data
}

export async function deletePresentation(id: string): Promise<void> {
	await api().delete(`/presentations/${id}`)
}

/** Copy any deck the caller can open into a new, personal deck of their own. */
export async function duplicatePresentation(id: string): Promise<Presentation> {
	const { data } = await api().post<Presentation>(`/presentations/${id}/duplicate`)
	return data
}

/**
 * The elements this user may place. With a scope, generic elements plus that
 * scope's own; without one, all of them, which the editor needs to render a
 * deck spanning several asset types.
 */
export async function getElements(scope?: PresentationScope): Promise<ElementDescriptor[]> {
	const { data } = await api().get<ElementDescriptor[]>('/presentations/elements', {
		params: scope ? { scope } : undefined,
	})
	return data
}

/**
 * Resolve a SAVED deck's blocks as the caller. Pass `slideIds` to resolve one
 * story at a time, which is how the presenter stays ahead of the audience.
 */
export async function resolvePresentation(id: string, slideIds?: string[]): Promise<ResolveResponse> {
	const { data } = await api().post<ResolveResponse>(
		`/presentations/${id}/resolve`,
		slideIds ? { slideIds } : {},
	)
	return data
}

/**
 * Resolve UNSAVED blocks, at most one slide's worth (6) per call. The editor
 * uses this for everything it shows, because what it is showing is usually
 * ahead of what is saved.
 */
export async function resolvePreview(
	blocks: PreviewBlockInput[],
	anchor?: EntityRef | null,
): Promise<ResolveResponse> {
	const { data } = await api().post<ResolveResponse>('/presentations/resolve-preview', {
		blocks,
		...(anchor ? { anchor } : {}),
	})
	return data
}

/**
 * The caller's assets of one type whose names contain `query`, at most 20,
 * owner or partner (evaluations owner-only). Lighter than the per-type list
 * endpoints, which return fully populated documents.
 */
export async function searchAssets(scope: PresentationScope, query = ''): Promise<AssetOption[]> {
	const { data } = await api().get<AssetOption[]>('/presentations/assets', { params: { scope, q: query } })
	return data
}

/**
 * Name the assets with these ids (MYD-495), e.g. a deck's story anchors. Ids
 * the caller cannot see are simply absent from the answer.
 */
export async function lookupAssets(scope: PresentationScope, ids: string[]): Promise<AssetOption[]> {
	if (ids.length === 0) return []
	const { data } = await api().get<AssetOption[]>('/presentations/assets', { params: { scope, ids: ids.join(',') } })
	return data
}

/**
 * The photos a location block can show (MYD-499): the location's own, or for
 * a project its location's. Newest first.
 */
export async function getSourcePhotos(source: EntityRef): Promise<SourcePhoto[]> {
	const { data } = await api().get<SourcePhoto[]>('/presentations/photos', { params: { scope: source.scope, entityId: source.entityId } })
	return data
}

/**
 * Record that the caller started presenting this deck (MYD-496). Recorded for
 * the owner only, and never bumps "last edited". Fire and forget: presenting
 * must not wait on it or fail because of it.
 */
export async function markPresented(id: string): Promise<void> {
	await api().post(`/presentations/${id}/presented`)
}

// =============================================================================
// Story templates (MYD-505)
// =============================================================================

/** The starters, and the caller's own saved templates. */
export async function listTemplates(scope?: PresentationScope): Promise<PresentationTemplate[]> {
	const { data } = await api().get<PresentationTemplate[]>('/presentations/templates', { params: scope ? { scope } : undefined })
	return data
}

/**
 * Stories made from a template for these assets, as the caller: elements they
 * may not see are already left out. Saves nothing; the editor inserts them.
 */
export async function instantiateTemplate(templateKey: string, sources: EntityRef[]): Promise<InstantiatedStories> {
	const { data } = await api().post<InstantiatedStories>('/presentations/templates/instantiate', { templateKey, sources })
	return data
}

/** Save a story as one of the caller's templates. Its slides travel; its asset does not. */
export async function saveStoryAsTemplate(input: { name: string; description?: string; story: Story }): Promise<PresentationTemplate> {
	const { data } = await api().post<PresentationTemplate>('/presentations/templates', input)
	return data
}

/** Delete one of the caller's own templates, of either kind. Starters cannot be. */
export async function deleteTemplate(key: string): Promise<void> {
	await api().delete(`/presentations/templates/${encodeURIComponent(key)}`)
}

/** Rename one of the caller's own templates, of either kind (MYD-602). */
export async function renameTemplate(key: string, input: { name?: string; description?: string }): Promise<PresentationTemplate> {
	const { data } = await api().patch<PresentationTemplate>(`/presentations/templates/${encodeURIComponent(key)}`, input)
	return data
}

// =============================================================================
// Deck templates (MYD-602)
// =============================================================================

/** The deck starters, and the caller's own saved deck templates. */
export async function listDeckTemplates(): Promise<PresentationTemplate[]> {
	const { data } = await api().get<PresentationTemplate[]>('/presentations/templates', { params: { kind: 'deck' } })
	return data
}

/**
 * A new deck, the caller's own, made from a deck template for one asset. The
 * name defaults to "<template>: <asset>" on the server.
 */
export async function createPresentationFromTemplate(input: { templateKey: string; anchor: EntityRef; name?: string }): Promise<DeckFromTemplate> {
	const { data } = await api().post<DeckFromTemplate>('/presentations/from-template', input)
	return data
}

/** Save one of the caller's decks as a deck template, from its saved state. */
export async function saveDeckAsTemplate(presentationId: string, input: { name: string; description?: string }): Promise<SavedDeckTemplate> {
	const { data } = await api().post<SavedDeckTemplate>(`/presentations/${encodeURIComponent(presentationId)}/save-as-template`, input)
	return data
}

// =============================================================================
// A portfolio review (MYD-507)
// =============================================================================

/** The portfolio's locations the caller may read, in the portfolio's order. */
export async function getPortfolioLocations(portfolioId: string): Promise<PortfolioLocations> {
	const { data } = await api().get<PortfolioLocations>('/presentations/portfolio-locations', { params: { portfolioId } })
	return data
}

/** The overview and location stories, for the editor to insert. Saves nothing. */
export async function getPortfolioStories(request: PortfolioDeckRequest & { existingSlides?: number }): Promise<InstantiatedStories> {
	const { data } = await api().post<InstantiatedStories>('/presentations/portfolio-stories', request)
	return data
}

/** A new deck: the overview, then a story per location. */
export async function createPresentationFromPortfolio(request: PortfolioDeckRequest & { name?: string }): Promise<Presentation & { dropped: number }> {
	const { data } = await api().post<Presentation & { dropped: number }>('/presentations/from-portfolio', request)
	return data
}

// =============================================================================
// From Explorer (MYD-506)
// =============================================================================

/** Most assets one stories/generate request takes; more are sent in batches. */
export const GENERATE_BATCH = 25

/** Append a story per asset to a deck the caller owns, and save. */
export async function generateStoriesInto(
	presentationId: string,
	templateKey: string,
	sources: EntityRef[],
): Promise<{ presentation: Presentation; added: number; skipped: EntityRef[]; dropped: number }> {
	const { data } = await api().post(`/presentations/${presentationId}/stories/generate`, { templateKey, sources })
	return data
}

/** The locations behind evaluation sites, once each; sites with none are counted. */
export async function locationsForSites(siteIds: string[]): Promise<{ locationIds: string[]; withoutLocation: number }> {
	const { data } = await api().post('/presentations/site-locations', { siteIds })
	return data
}

// =============================================================================
// Published snapshots (MYD-508)
// =============================================================================

/** A deck's snapshots, newest first, and what publishing now would involve. Owner only. */
export async function listSnapshots(presentationId: string): Promise<{ preflight: SnapshotPreflight; snapshots: SnapshotSummary[] }> {
	const { data } = await api().get(`/presentations/${presentationId}/snapshots`)
	return data
}

/** Publish the SAVED deck, with the images captured of its map and chart blocks. */
export async function createSnapshot(presentationId: string, rasters: Record<string, string>): Promise<SnapshotSummary> {
	const { data } = await api().post(`/presentations/${presentationId}/snapshots`, { rasters })
	return data
}

export async function getSnapshot(presentationId: string, version: number): Promise<Snapshot> {
	const { data } = await api().get(`/presentations/${presentationId}/snapshots/${version}`)
	return data
}

// =============================================================================
// Public share links (MYD-509)
// =============================================================================

/** A deck's links, newest first, and the lifetimes a link may have. Owner only. */
export async function listShareLinks(presentationId: string): Promise<{ links: ShareLink[]; ttlChoices: number[]; defaultTtlDays: number }> {
	const { data } = await api().get(`/presentations/${presentationId}/share-links`)
	return data
}

export async function createShareLink(presentationId: string, version: number, ttlDays: number): Promise<ShareLink> {
	const { data } = await api().post(`/presentations/${presentationId}/share-links`, { version, ttlDays })
	return data
}

export async function revokeShareLink(presentationId: string, linkId: string): Promise<ShareLink> {
	const { data } = await api().put(`/presentations/${presentationId}/share-links/${linkId}/revoke`)
	return data
}

/**
 * What a link's recipient sees. Plain axios, NOT the app's interceptor: the
 * recipient is not signed in, and the interceptor would attach a session (or,
 * on a 401, send them to the login screen).
 */
export async function getSharedPresentation(token: string): Promise<SharedPresentation> {
	try {
		const { data } = await axios.get<SharedPresentation>(`${API_BASE_URL}/public/presentations/${encodeURIComponent(token)}`)
		return data
	} catch (cause: any) {
		const status = cause?.response?.status
		const code = cause?.response?.data?.code
		const reason: SharedPresentationError =
			status === 429 ? 'rate_limited' : status === 404 ? 'not_found' : ['expired', 'revoked', 'unavailable'].includes(code) ? code : 'failed'
		throw new SharedPresentationLoadError(reason)
	}
}

export class SharedPresentationLoadError extends Error {
	constructor(public readonly reason: SharedPresentationError) {
		super(reason)
		this.name = 'SharedPresentationLoadError'
	}
}

// =============================================================================
// PDF export (MYD-510)
// =============================================================================

/** For the print page only: the snapshot a short-lived print token names. Plain axios, signed out. */
export async function getPrintPresentation(printToken: string): Promise<SharedPresentation> {
	const { data } = await axios.get<SharedPresentation>(`${API_BASE_URL}/public/presentations/print/${encodeURIComponent(printToken)}`)
	return data
}

/** Where a PDF may be filed when the deck is about several assets. */
export interface FilingOption {
	scope: PresentationScope
	entityId: string
	name: string
}

export type ExportPdfResult =
	| { kind: 'pdf'; blob: Blob; filename: string; filed: 'none' | 'failed' | string }
	| { kind: 'choose'; options: FilingOption[] }

/**
 * Export a published version as a PDF. The server files it on the asset's
 * Documents tab when the deck is about one asset; about several, it answers
 * `choose`, and the caller asks the owner and calls again with `fileTo`.
 */
export async function exportSnapshotPdf(presentationId: string, version: number, fileTo?: 'none' | { scope: PresentationScope; entityId: string }): Promise<ExportPdfResult> {
	return exportSnapshotFile(presentationId, version, 'pdf', fileTo)
}

/**
 * Export a published version as PowerPoint (MYD-512): editable text and
 * tables, pictures of the maps and charts, the speaker notes. Downloaded
 * only; it is never filed.
 */
export async function exportSnapshotPptx(presentationId: string, version: number): Promise<ExportPdfResult> {
	return exportSnapshotFile(presentationId, version, 'pptx')
}

async function exportSnapshotFile(
	presentationId: string,
	version: number,
	format: 'pdf' | 'pptx',
	fileTo?: 'none' | { scope: PresentationScope; entityId: string },
): Promise<ExportPdfResult> {
	try {
		const response = await api().post(`/presentations/${presentationId}/snapshots/${version}/export`, fileTo === undefined ? {} : { fileTo }, {
			params: { format },
			responseType: 'blob',
		})
		const disposition = String(response.headers['content-disposition'] ?? '')
		const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1]
		const filename = encoded ? decodeURIComponent(encoded) : `Presentation v${version}.${format}`
		return { kind: 'pdf', blob: response.data, filename, filed: String(response.headers['x-filed'] ?? 'none') }
	} catch (cause: any) {
		// A blob response hides the JSON error body: read it back.
		const data = cause?.response?.data
		const body = data instanceof Blob ? JSON.parse((await data.text()) || '{}') : data
		if (cause?.response?.status === 409 && body?.code === 'choose_asset') return { kind: 'choose', options: body.options ?? [] }
		const what = format === 'pptx' ? 'The PowerPoint file' : 'The PDF'
		throw Object.assign(new Error(body?.message ?? `${what} could not be made.`), { response: { ...cause?.response, data: body } })
	}
}

// =============================================================================
// Sharing with partners (MYD-526)
// =============================================================================

/** Who a deck is shared with, and the partners it could be. Owner only. */
export async function getSharing(presentationId: string): Promise<DeckSharing> {
	const { data } = await api().get<DeckSharing>(`/presentations/${presentationId}/sharing`)
	return data
}

/** Share with nobody, all partners, or chosen partners. `sharedWith` omitted keeps the stored choice. */
/** `notify: false` shares quietly; the server tells newly added partners otherwise (MYD-527). */
export async function updateSharing(presentationId: string, input: { visibility: PresentationVisibility; sharedWith?: string[]; notify?: boolean }): Promise<DeckSharing> {
	const { data } = await api().put<DeckSharing>(`/presentations/${presentationId}/sharing`, input)
	return data
}
